import { getCurrentInstance, type CSSProperties, type Ref, ref, watch, computed, isRef } from 'vue'
import { unique } from '../array'
import { useElementHover } from '@vueuse/core'
import { unwrapElementRef } from '../dom/unwrap'
import { isFunction } from '../is'
import { uniqueBy } from 'math'
import { containsNumber } from '../string'

type Dict = { [key: string]: any }
type KeyType = string | symbol | number

type MergeOptions = {
  preserveClasses?: boolean;
  removeClasses?: string[]
}

type Variant = {
  class?: string
  unclass?: string[]
  style?: CSSProperties
  hover?: Omit<Variant, 'hover'>
  props?: Dict
  options?: MergeOptions
}

type ComputedVariant = Omit<Variant, 'props'> & {
  ref: Ref
  props: Dict
}

type ConditionalVariant<ConditionKey extends KeyType, SlotName> = Record<
  ConditionKey,
  SlotName extends KeyType ? Record<SlotName, Variant | ((props: Dict) => Variant)> : never
>
//  def: VariantDefinition<string | keyof T, keyof T>
export type VariantDefinition<T extends Dict = Dict> = {
  slots: Record<keyof T, Variant | string>
  variants: Record<string | keyof T, ConditionalVariant<string, keyof T>>
}

type VariantProps<T extends Dict> = Record<keyof T, any>

type ClassState = {
  hover: boolean
}
type ClassRef = {
  ref: Ref
  state: Ref<ClassState>
}

const split = (v?: string): string[] => (v ? (v.includes(' ') ? v.split(' ') : [v]) : [])
const unsplit = (splits: string[]) => splits.join(' ')
const isDict = (x: any): x is Dict => typeof x === 'object'

const merge = (a: Dict, b: Dict): Dict => {
  const keys = unique([...Object.keys(a), ...Object.keys(b)])
  return keys.reduce((prev, cur) => {
    const va = a[cur]
    const vb = b[cur]
    const v =
      typeof va === 'undefined' && typeof vb !== 'undefined'
        ? vb
        : typeof vb === 'undefined' && typeof va !== 'undefined'
          ? va
          : isDict(va) && isDict(vb)
            ? merge(va, vb)
            : Array.isArray(va) && Array.isArray(vb)
              ? unique([...va, ...vb])
              : vb || va
    return {
      ...prev,
      [cur]: v
    }
  }, a)
}

const classID = (x: string): string => {
  if (x.startsWith('bg-')) return 'bg-'
  if (x.startsWith('text-') && containsNumber(x)) return 'text-'
  return x
}

const mergeClasses = (a: string[], b: string[], options?: MergeOptions): string[] => {
  const toRemove = options?.removeClasses || [];
  const copyA = toRemove.length <= 0 ? [...a] : a.filter(it => toRemove.includes(it) === false);
  const copyB = [...b];

    
  
  if (!options?.preserveClasses) {
    //  const unset = b.filter(it => it.includes('-')).map(it => it.split('-')[0])
    // unset.forEach(un => { a = a.filter(it => it.startsWith(un)) })
  }

  return uniqueBy([...copyB, ...copyA], (it) => classID(it))
}

const mergeVariantsMulti = (variants: Variant[]): Variant => {
  return variants.reduce((prev, cur) => {
    const unclass = cur.unclass || []
    return {
      hover: cur.hover || prev.hover,
      class: unsplit(
        mergeClasses(split(prev.class), split(cur.class), cur.options).filter(
          (it) => unclass.includes(it) === false
        )
      ),
      style: merge(prev.style || {}, cur.style || {}),
      props: merge(prev.props || {}, cur.props || {})
    }
  })
}



const uref = (x: any): any => {
  if (isRef(x)) {
    const val = x.value
    if (isRef(val)) return uref(val)
    return val
  }
  return x
}

export const defineVariants = <T extends Dict = Dict>(
  def: VariantDefinition<T>
) => {


  return (
    props: VariantProps<typeof def.variants>
  ): Ref<Record<keyof typeof def.slots, ComputedVariant>> => {
    const getProp = <T = any>(props: T, key: string): any => {
      const v = (props as any)[key]
      if (isRef(v)) {
        return uref(v)
      }
      return v
    }

    const refs: Record<keyof typeof def.slots, ClassRef> = Object.assign(
    {} as Record<keyof typeof def.slots, ClassRef>,
    ...Object.keys(def.slots).map((k) => ({
      [k]: {
        ref: ref(),
        state: ref({
          hover: false
        })
      }
    }))
  )

  Object.values(refs).forEach((cls) => {
    const h = useElementHover(cls.ref)

    watch(
      [cls.ref, h],
      ([k, hv]) => {
        const el = unwrapElementRef(k) as HTMLElement
        if (!el) return
        cls.state.value.hover = hv
      },
      { immediate: true, deep: true }
    )
  })

    const instance = getCurrentInstance()
    if (!instance) throw new Error(`Must be used in a component`)

    const getChanges = (props: VariantProps<typeof def.variants>, dname: string) => {
      return Object.keys(def.variants)
        .map((varkey) => {
          const vari = def.variants[varkey] || {};
          const variDef = (vari[getProp(props, varkey)] || {}) || ({} as Record<string, Variant>);
          const v_ = variDef[dname as keyof typeof variDef];
          const v = isFunction(v_) ? v_(props) : v_
          if (v?.hover && (refs[dname].state.value.hover)) {
            return mergeVariantsMulti([v, v.hover])
          }
          return v
        })
        .filter((it) => !!it)
    }
    return computed(() =>
      Object.entries(def.slots).reduce(
        (prev, [slotKey, defSlot]) => {
          return {
            ...prev,
            [slotKey]: {
              ...mergeVariantsMulti([
                typeof defSlot === 'string' ? { class: defSlot, props: {} } : defSlot,
                ...getChanges({ ...props, hover: refs[slotKey].state.value.hover }, slotKey)
              ]),
              ref: refs[slotKey].ref
            }
          }
        },
        {} as Record<keyof typeof def.slots, ComputedVariant>
      )
    )
  }
}
